import { useGetAICreditsSummaryQuery } from "@/app/features/recruitment/dashboard/recruitment-dashboard.api";
import type { AiCreditSummaryRecord } from "@/app/features/recruitment/dashboard/recruitment-dashboard.type";
import useAuth from "@/hooks/useAuth";
import { AiFileIcon, StarsIcon, VideoCameraAiIcon } from "@/icons";
import { Card, Flex, Skeleton, Tooltip } from "antd";
import dayjs from "dayjs";

// ── Helpers ───────────────────────────────────────────────────────────────────

const FEATURE_META: Record<
  string,
  { label: string; icon: React.ReactNode; color: string; accent: string }
> = {
  AI_INTERVIEW: {
    label: "AI Interview",
    icon: <VideoCameraAiIcon size={20} className="text-[#2CA2C1]" />,
    color: "#2CA2C1",
    accent: "rgba(44,162,193,0.12)",
  },
  TALENT_HUNT: {
    label: "TalentHunt",
    icon: <AiFileIcon size={20} className="text-[#FFC300]" />,
    color: "#FFC300",
    accent: "rgba(255,195,0,0.12)",
  },
  AI_QUESTION: {
    label: "AI Question",
    icon: <StarsIcon size={20} className="text-[#A78BFA]" />,
    color: "#A78BFA",
    accent: "rgba(167,139,250,0.12)",
  },
};

function getRingProps(used: number, total: number) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const radius = 38;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  return { pct, radius, circumference, offset };
}

// ── Sub-component: Credit Ring ─────────────────────────────────────────────────

function CreditRing({
  used,
  total,
  color,
}: {
  used: number;
  total: number;
  color: string;
}) {
  const { pct, radius, circumference, offset } = getRingProps(used, total);

  return (
    <Tooltip
      title={`${used.toLocaleString()} used out of ${total.toLocaleString()} allocated`}
    >
      <div className="relative flex-shrink-0" style={{ width: 96, height: 96 }}>
        <svg width="96" height="96" viewBox="0 0 96 96">
          {/* Track */}
          <circle
            cx="48"
            cy="48"
            r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.07)"
            strokeWidth="8"
          />
          {/* Progress */}
          <circle
            cx="48"
            cy="48"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform="rotate(-90 48 48)"
            style={{ transition: "stroke-dashoffset 0.6s ease" }}
          />
        </svg>
        {/* Center label */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-white font-bold text-base leading-none">
            {Math.round(pct)}%
          </span>
          <span className="text-gray-400 text-[10px] mt-0.5">Used</span>
        </div>
      </div>
    </Tooltip>
  );
}

// ── Sub-component: Skeleton Card ──────────────────────────────────────────────

function CreditCardSkeleton() {
  return (
    <Card
      className="dark:bg-theme-slate-800 border dark:border-theme-gray-700 rounded-xl shadow-md overflow-hidden"
      styles={{ body: { padding: 0 } }}
    >
      <div className="h-1 w-full bg-white/10" />
      <div className="p-5 space-y-4">
        <Skeleton active paragraph={{ rows: 0 }} />
        <Skeleton.Node active className="!w-full !h-[80px] !rounded-xl" />
      </div>
    </Card>
  );
}

// ── Sub-component: Credit Card ────────────────────────────────────────────────

function CreditCard({
  featureType,
  record,
}: {
  featureType: string;
  record: AiCreditSummaryRecord;
}) {
  const meta = FEATURE_META[featureType] ?? {
    label: featureType,
    icon: <StarsIcon size={20} className="text-gray-300" />,
    color: "#6b7280",
    accent: "rgba(107,114,128,0.12)",
  };

  const latestExpiry = dayjs(record.latest_expiry_date);
  const now = dayjs();
  const isExpired = !record.latest_expiry_date || latestExpiry.isBefore(now);
  const daysLeft = latestExpiry.diff(now, "day");
  const isExpiringSoon = daysLeft <= 90 && !isExpired;

  return (
    <Card
      className="dark:bg-theme-slate-800 border dark:border-theme-gray-700 rounded-xl shadow-md overflow-hidden"
      styles={{ body: { padding: 0 } }}
    >
      {/* Accent top bar */}
      <div className="h-1 w-full" style={{ background: meta.color }} />

      <div className="p-5">
        {/* Header row */}
        <Flex align="center" justify="space-between" gap={12} className="mb-5">
          <Flex align="center" gap={10}>
            {/* Icon badge */}
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: meta.accent }}
            >
              {meta.icon}
            </div>
            <div>
              <div className="text-white font-bold text-base leading-tight">
                {meta.label}
              </div>
              <div className="text-gray-400 text-xs mt-0.5">AI Credits</div>
            </div>
          </Flex>

          {/* Batches badge */}
          {/* <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: meta.accent, color: meta.color }}
          >
            <span>{record.active_batches_count}</span>
            <span>Active {record.active_batches_count === 1 ? "Batch" : "Batches"}</span>
          </div> */}
        </Flex>

        {/* Ring + stats row */}
        <Flex align="center" gap={20} className="mb-2">
          <CreditRing
            used={record.total_used_credit}
            total={record.total_allocated_credit}
            color={meta.color}
          />

          <div className="flex-1 grid grid-cols-2 gap-3">
            {/* Available */}
            <div
              className="rounded-lg p-3"
              style={{ background: "rgba(255,255,255,0.04)" }}
            >
              <div className="text-gray-400 text-xs mb-1">Available</div>
              <div className="text-white font-bold text-xl leading-none">
                {record.total_available_credit.toLocaleString()}
              </div>
              <div className="text-gray-500 text-[10px] mt-1">Credits</div>
            </div>

            {/* Used */}
            {/* <div
              className="rounded-lg p-3"
              style={{ background: "rgba(255,255,255,0.04)" }}
            >
              <div className="text-gray-400 text-xs mb-1">Consumed</div>
              <div
                className="font-bold text-xl leading-none"
                style={{ color: meta.color }}
              >
                {record.total_used_credit.toLocaleString()}
              </div>
              <div className="text-gray-500 text-[10px] mt-1">Credits</div>
            </div> */}

            {/* Latest expiry spanning full width */}
            <div
              className="col-span-2 rounded-lg p-3"
              style={{ background: "rgba(255,255,255,0.04)" }}
            >
              <div className="text-gray-400 text-xs mb-1">Latest Expiry</div>
              <div
                className={`font-bold text-sm leading-none ${
                  isExpired
                    ? "text-red-400"
                    : isExpiringSoon
                      ? "text-yellow-400"
                      : "text-white"
                }`}
              >
                {isExpired ? "Expired" : latestExpiry.format("DD MMM YYYY")}
              </div>
              <div
                className={`text-[10px] mt-1 ${
                  isExpired
                    ? "text-red-400/70"
                    : isExpiringSoon
                      ? "text-yellow-400/70"
                      : "text-gray-500"
                }`}
              >
                {isExpired
                  ? ""
                  : daysLeft === 0
                    ? "Expires today"
                    : `${daysLeft} ${daysLeft === 1 ? "day" : "days"} remaining`}
              </div>
            </div>
          </div>
        </Flex>
      </div>
    </Card>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function AiCreditsSummary() {
  const { organizationId } = useAuth();
  const { data, isLoading, isFetching } = useGetAICreditsSummaryQuery(
    organizationId,
    { skip: !organizationId },
  );

  const loading = isLoading || isFetching;
  const summary = data?.data?.summary ?? {};
  const featureEntries = Object.entries(summary);

  // Aggregate totals across all feature types
  const totalAllocated = featureEntries.reduce(
    (acc, [, r]) => acc + r.total_allocated_credit,
    0,
  );
  const totalUsed = featureEntries.reduce(
    (acc, [, r]) => acc + r.total_used_credit,
    0,
  );
  const totalAvailable = featureEntries.reduce(
    (acc, [, r]) => acc + r.total_available_credit,
    0,
  );
  const overallPct =
    totalAllocated > 0 ? Math.round((totalUsed / totalAllocated) * 100) : 0;

  return (
    <div className="space-y-5">
      {/* Section header */}
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={10}>
          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
            <StarsIcon size={18} className="text-white" />
          </div>
          <div>
            <div className="text-white font-bold text-lg leading-tight">
              AI Credits
            </div>
            <div className="text-gray-400 text-xs">
              Usage overview across all AI features
            </div>
          </div>
        </Flex>

        {/* Overall summary pill */}
        {!loading && featureEntries.length > 0 && (
          <div className="flex flex-col items-end gap-1.5">
            {/* Progress track */}
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-xs">{overallPct}% used</span>
              <div
                className="w-28 h-1.5 rounded-full overflow-hidden"
                style={{ background: "rgba(255,255,255,0.1)" }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${overallPct}%`,
                    background:
                      overallPct >= 90
                        ? "#f87171"
                        : overallPct >= 70
                          ? "#facc15"
                          : "#2CA2C1",
                    transition: "width 0.5s ease",
                  }}
                />
              </div>
            </div>
            {/* Labels */}
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-400">
                Used{" "}
                <span className="text-white font-semibold">
                  {totalUsed.toLocaleString()}
                </span>{" "}
              </span>
              <span className="text-gray-600">·</span>
              <span className="text-gray-400">
                Available{" "}
                <span className="text-white font-semibold">
                  {totalAvailable.toLocaleString()}
                </span>{" "}
              </span>
              <span className="text-gray-600">·</span>
              <span className="text-gray-400">
                Total{" "}
                <span className="text-white">
                  {totalAllocated.toLocaleString()}
                </span>
              </span>
            </div>
          </div>
        )}
      </Flex>

      {/* Individual credit cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
        {loading
          ? Array.from({ length: 3 }).map((_, i) => (
              <CreditCardSkeleton key={i} />
            ))
          : featureEntries.map(([featureType, record]) => (
              <CreditCard
                key={featureType}
                featureType={featureType}
                record={record}
              />
            ))}
      </div>
    </div>
  );
}
